import { faArrowAltCircleRight, faQrcode } from '@fortawesome/free-solid-svg-icons';
import { faBarcode } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import PropTypes from 'prop-types';
import React from 'react';
import { BUREAU_BARCODE_FORMAT, MEASUREMENT_SYSTEM } from 'src/utils/constants';
import { formatUnits } from 'src/utils/conversions';
import { PIECE_SCAN_VIEW_OPTIONS } from 'src/utils/pieceScanViewPreference';

const SettingsCard = ({
  debugMode,
  onDebugModeChange,
  showKioskMode,
  kioskEnabled,
  onKioskModeChange,
  scanMode,
  onScanModeSelect,
  autoParsePreference,
  onAutoParsePreferenceChange,
  pieceScanViewRemember,
  pieceScanView,
  onPieceScanViewRememberChange,
  onPieceScanViewChange,
}) => {
  return (
    <div className='settings-card'>
      <div className='settings-card-header'>
        <h3>App Settings</h3>
      </div>

      <div className='settings-card-body'>
        <div className='setting-group'>
          <div className='setting-item'>
            <label className='toggle-label'>
              <span>Debug Mode</span>
              <input
                type='checkbox'
                className='toggle-input'
                checked={debugMode}
                onChange={onDebugModeChange}
              />
              <span className='toggle-slider' />
            </label>
          </div>

          {debugMode && showKioskMode && (
            <div className='setting-item'>
              <label className='toggle-label'>
                <span>Kiosk Mode</span>
                <input
                  type='checkbox'
                  className='toggle-input'
                  checked={kioskEnabled}
                  onChange={onKioskModeChange}
                />
                <span className='toggle-slider' />
              </label>
            </div>
          )}

          <div className='setting-item-vertical'>
            <span className='setting-title'>Auto-Parse Saved Option</span>
            <div className='measurement-pills'>
              <button
                type='button'
                className={`pill ${autoParsePreference === 'ask' ? 'active' : ''}`}
                onClick={() => onAutoParsePreferenceChange('ask')}
              >
                Ask
              </button>
              <button
                type='button'
                className={`pill ${autoParsePreference === 'parse' ? 'active' : ''}`}
                onClick={() => onAutoParsePreferenceChange('parse')}
              >
                Parse
              </button>
              <button
                type='button'
                className={`pill ${autoParsePreference === 'skip' ? 'active' : ''}`}
                onClick={() => onAutoParsePreferenceChange('skip')}
              >
                Skip
              </button>
            </div>
          </div>
        </div>

        <div className='setting-group'>
          <div className='setting-item-vertical'>
            <span className='setting-title'>Scan Mode</span>
            <div className='scan-mode-pills'>
              <button
                type='button'
                className={`pill ${scanMode === BUREAU_BARCODE_FORMAT.QR ? 'active' : ''}`}
                onClick={() => onScanModeSelect(BUREAU_BARCODE_FORMAT.QR)}
              >
                <FontAwesomeIcon icon={faQrcode} />
                QR
              </button>
              <button
                type='button'
                className={`pill ${scanMode === BUREAU_BARCODE_FORMAT.BARCODE ? 'active' : ''}`}
                onClick={() => onScanModeSelect(BUREAU_BARCODE_FORMAT.BARCODE)}
              >
                <FontAwesomeIcon icon={faBarcode} />
                Barcode
              </button>
            </div>
          </div>
        </div>

        <div className='setting-group'>
          <div className='setting-item'>
            <label className='toggle-label'>
              <span>Remember piece scan view</span>
              <input
                type='checkbox'
                className='toggle-input'
                checked={pieceScanViewRemember}
                onChange={onPieceScanViewRememberChange}
              />
              <span className='toggle-slider' />
            </label>
          </div>

          {pieceScanViewRemember && (
            <div className='setting-item-vertical'>
              <span className='setting-title'>Default piece scan destination</span>
              <div className='measurement-pills'>
                <button
                  type='button'
                  className={`pill ${pieceScanView === PIECE_SCAN_VIEW_OPTIONS.RUN ? 'active' : ''}`}
                  onClick={() => onPieceScanViewChange(PIECE_SCAN_VIEW_OPTIONS.RUN)}
                >
                  <FontAwesomeIcon icon={faArrowAltCircleRight} />
                  Current Run
                </button>
                <button
                  type='button'
                  className={`pill ${pieceScanView === PIECE_SCAN_VIEW_OPTIONS.PIECE ? 'active' : ''}`}
                  onClick={() => onPieceScanViewChange(PIECE_SCAN_VIEW_OPTIONS.PIECE)}
                >
                  <FontAwesomeIcon icon={faQrcode} />
                  Piece Page
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

SettingsCard.propTypes = {
  debugMode: PropTypes.bool.isRequired,
  onDebugModeChange: PropTypes.func.isRequired,
  showKioskMode: PropTypes.bool,
  kioskEnabled: PropTypes.bool,
  onKioskModeChange: PropTypes.func,
  scanMode: PropTypes.string.isRequired,
  onScanModeSelect: PropTypes.func.isRequired,
  autoParsePreference: PropTypes.oneOf(['ask', 'parse', 'skip']).isRequired,
  onAutoParsePreferenceChange: PropTypes.func.isRequired,
  pieceScanViewRemember: PropTypes.bool.isRequired,
  pieceScanView: PropTypes.oneOf([PIECE_SCAN_VIEW_OPTIONS.RUN, PIECE_SCAN_VIEW_OPTIONS.PIECE])
    .isRequired,
  onPieceScanViewRememberChange: PropTypes.func.isRequired,
  onPieceScanViewChange: PropTypes.func.isRequired,
};

SettingsCard.defaultProps = {
  showKioskMode: false,
  kioskEnabled: false,
  onKioskModeChange: () => {},
};

const UnitSelector = ({ label, value, units, onChange }) => (
  <div className='unit-selector-row'>
    <span className='unit-label'>{label}</span>
    <select className='unit-select' value={value} onChange={e => onChange(e.target.value)}>
      {units.map(unit => (
        <option key={unit} value={unit}>
          {formatUnits(unit)}
        </option>
      ))}
    </select>
  </div>
);

UnitSelector.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  units: PropTypes.arrayOf(PropTypes.string).isRequired,
  onChange: PropTypes.func.isRequired,
};

// Measurement Units Settings Card
const MeasurementSettingsCard = ({
  measurementSystem,
  onMeasurementSystemChange,
  volumeUnit,
  weightUnit,
  lengthUnit,
  areaUnit,
  volumeUnits,
  weightUnits,
  lengthUnits,
  areaUnits,
  onVolumeUnitChange,
  onWeightUnitChange,
  onLengthUnitChange,
  onAreaUnitChange,
}) => {
  return (
    <div className='settings-card'>
      <div className='settings-card-header'>
        <h3>Measurement Units</h3>
      </div>

      <div className='settings-card-body'>
        <div className='setting-group'>
          <div className='setting-item-vertical'>
            <span className='setting-title'>System</span>
            <div className='measurement-pills'>
              <button
                type='button'
                className={`pill ${measurementSystem === MEASUREMENT_SYSTEM.IMPERIAL ? 'active' : ''}`}
                onClick={() => onMeasurementSystemChange(MEASUREMENT_SYSTEM.IMPERIAL)}
              >
                Imperial
              </button>
              <button
                type='button'
                className={`pill ${measurementSystem === MEASUREMENT_SYSTEM.METRIC ? 'active' : ''}`}
                onClick={() => onMeasurementSystemChange(MEASUREMENT_SYSTEM.METRIC)}
              >
                Metric
              </button>
              <button
                type='button'
                className={`pill ${measurementSystem === MEASUREMENT_SYSTEM.CUSTOM ? 'active' : ''}`}
                onClick={() => onMeasurementSystemChange(MEASUREMENT_SYSTEM.CUSTOM)}
              >
                Custom
              </button>
            </div>
          </div>
        </div>

        {measurementSystem === MEASUREMENT_SYSTEM.CUSTOM && (
          <div className='setting-group'>
            <UnitSelector
              label='Volume Unit:'
              value={volumeUnit}
              units={volumeUnits}
              onChange={onVolumeUnitChange}
            />

            <UnitSelector
              label='Weight Unit:'
              value={weightUnit}
              units={weightUnits}
              onChange={onWeightUnitChange}
            />

            <UnitSelector
              label='Length Unit:'
              value={lengthUnit}
              units={lengthUnits}
              onChange={onLengthUnitChange}
            />

            <UnitSelector
              label='Area Unit:'
              value={areaUnit}
              units={areaUnits}
              onChange={onAreaUnitChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

MeasurementSettingsCard.propTypes = {
  measurementSystem: PropTypes.string.isRequired,
  onMeasurementSystemChange: PropTypes.func.isRequired,
  volumeUnit: PropTypes.string.isRequired,
  weightUnit: PropTypes.string.isRequired,
  lengthUnit: PropTypes.string.isRequired,
  areaUnit: PropTypes.string.isRequired,
  volumeUnits: PropTypes.array.isRequired,
  weightUnits: PropTypes.array.isRequired,
  lengthUnits: PropTypes.array.isRequired,
  areaUnits: PropTypes.array.isRequired,
  onVolumeUnitChange: PropTypes.func.isRequired,
  onWeightUnitChange: PropTypes.func.isRequired,
  onLengthUnitChange: PropTypes.func.isRequired,
  onAreaUnitChange: PropTypes.func.isRequired,
};

export default SettingsCard;
export { MeasurementSettingsCard };
